import * as React from "react";
import isEqual from "lodash.isequal";

export const KEY_CODES = {
  TAB: 9,
  ENTER: 13,
  BACKSPACE: 8,
  SPACE: 32,
  SHIFT: 16,
  LEFT_ARROW: 37,
  ESCAPE: 27,
  UP_ARROW: 38,
  RIGHT_ARROW: 39,
  DOWN_ARROW: 40,
};

interface Modifiers {
  shift?: boolean;
  alt?: boolean;
  ctrl?: boolean;
  meta?: boolean;
}

interface Handler {
  keyCodes: number[];
  handler: (event: React.KeyboardEvent<HTMLInputElement>) => void | boolean;
  modifiers?: Modifiers;
  exactMatch?: boolean; // Prevents handler firing if other keys depressed
}

interface HandlerProps {
  children: any;
  handlers: Handler[];
  captureOnWindow?: boolean;
  overrideTab?: boolean;
  resetOnFocus?: boolean; // If wrapping an input, this can be useful
}

type State = {
  [code: number]: boolean;
};

type Action =
  | {
      type: "down" | "up";
      payload: number;
    }
  | {
      type: "reset";
    };

const initialState: State = {};

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case "down":
      return { ...state, [action.payload]: true };
    case "up":
      return { ...state, [action.payload]: false };
    case "reset":
      return {};
    default:
      throw new Error();
  }
};

const getActiveCombos = (
  handlers: Handler[],
  state: State,
  activeModifiers: Modifiers
) =>
  handlers.map(({ keyCodes, exactMatch, modifiers }) => {
    const comboActive = keyCodes.every((keyCode) => state[keyCode]);
    const shift = modifiers ? modifiers.shift || false : false;
    const alt = modifiers ? modifiers.alt || false : false;
    const ctrl = modifiers ? modifiers.ctrl || false : false;
    const meta = modifiers ? modifiers.meta || false : false;

    if (
      shift !== activeModifiers.shift ||
      ctrl !== activeModifiers.ctrl ||
      meta !== activeModifiers.meta ||
      alt !== activeModifiers.alt
    ) {
      return false;
    }

    if (exactMatch && comboActive) {
      const comboLength = keyCodes.length;
      const totalKeysPressed = Object.values(state).filter(
        (isPressed) => isPressed
      ).length;

      return comboLength === totalKeysPressed ? true : false;
    }

    return comboActive;
  });

export const KeypressHandler = ({
  children,
  handlers,
  overrideTab: controlTab,
  resetOnFocus,
  captureOnWindow,
}: HandlerProps) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);

  const handleFocus = () => {
    if (resetOnFocus) {
      dispatch({ type: "reset" });
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const action: Action = { type: "down", payload: event.keyCode };
    const nextState = reducer(state, action);

    if (isEqual(state, nextState)) {
      return;
    }

    const modifiers = {
      shift: event.shiftKey,
      alt: event.altKey,
      ctrl: event.ctrlKey,
      meta: event.metaKey,
    };
    const activeCombos = getActiveCombos(handlers, nextState, modifiers);

    activeCombos.forEach((comboActive, i) => {
      if (comboActive) {
        const { handler, keyCodes } = handlers[i];
        const shouldPropogate = handler(event);
        const isTab = keyCodes.length === 1 && keyCodes[0] === KEY_CODES.TAB;

        if (shouldPropogate === false || (isTab && controlTab)) {
          event.preventDefault();
        }
      }
    });

    dispatch(action);
  };

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    dispatch({ type: "up", payload: event.keyCode });
  };

  React.useEffect(
    () => {
      if (captureOnWindow) {
        window.addEventListener(
          "keydown",
          handleKeyDown as unknown as EventListenerOrEventListenerObject
        );
        window.addEventListener(
          "keyup",
          handleKeyUp as unknown as EventListenerOrEventListenerObject
        );
      }

      return () => {
        window.removeEventListener(
          "keydown",
          handleKeyDown as unknown as EventListenerOrEventListenerObject
        );
        window.removeEventListener(
          "keyup",
          handleKeyUp as unknown as EventListenerOrEventListenerObject
        );
      };
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [captureOnWindow]
  );

  return (
    <span
      onKeyDownCapture={handleKeyDown}
      onKeyUpCapture={handleKeyUp}
      onFocusCapture={handleFocus}
    >
      {children}
    </span>
  );
};
